import { Track } from '../../types/Track';

export type CrateContentsEntry = {
  file: string;
  name: string;
  tracks: string[];
};

/** Portable path keys used to match library files to crate entries. */
export type CrateIndex = Map<string, string[]>;

/**
 * Serato stores paths without a drive letter or leading slash. Library tracks
 * use absolute paths, so both sides are normalised the same way before matching.
 */
export const toPortablePath = (filePath: string): string => {
  const normalized = filePath.replace(/\\/g, '/');
  if (/^[a-zA-Z]:/.test(normalized)) {
    return normalized.replace(/^[a-zA-Z]:\//, '');
  }
  if (normalized.startsWith('/')) {
    return normalized.substring(1);
  }
  return normalized;
};

export const pathMatchKey = (filePath: string): string =>
  toPortablePath(filePath).toLowerCase();

/** One pass over every crate builds a lookup the whole page can share. */
export const buildCrateIndex = (crates: CrateContentsEntry[]): CrateIndex => {
  const index: CrateIndex = new Map();

  crates.forEach((crate) => {
    crate.tracks.forEach((trackPath) => {
      const key = pathMatchKey(trackPath);
      const names = index.get(key) ?? [];
      if (!names.includes(crate.name)) {
        names.push(crate.name);
      }
      index.set(key, names);
    });
  });

  return index;
};

export const cratesForFile = (
  index: CrateIndex | null,
  filePath: string,
): string[] => {
  if (!index) return [];
  return index.get(pathMatchKey(filePath)) ?? [];
};

export const cratesForTrack = (
  index: CrateIndex | null,
  track: Track,
): string[] => cratesForFile(index, track.filePath);
